"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import { SiteData, WebsiteTesterProps } from "../src/types/website-tester";
import SeoAuditHeader from 'components/reuseable/website-tester-intro'
import useLogVisitor from 'hooks/useLogVisitor.js';
import ScreenCanvas from "components/reuseable/ScreenCanvas.tsx";
// import { config } from "../src/config/website-component-config";
import { config } from "components/reuseable/screen-canvas-config";

const WebsiteTester: React.FC<WebsiteTesterProps> = () => {
    const { bgColor, screenshotWidth, screenshotHeight, PADDING, extraVerticalPadding, industrySector } = config;
    useLogVisitor();
    const router = useRouter();
    const [siteKey, setSiteKey] = useState<string | null>(null);
    const [fontSize, setFontSize] = useState<number>(34); // Default font size
    const [siteData, setSiteData] = useState<SiteData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        const siteKeyParam = query.get("visitor");
        const fontSizeParam = query.get("fontSize");

        if (siteKeyParam) {
            setSiteKey(siteKeyParam);
        } else {
            setError("Site key is missing from the URL.");
            setLoading(false);
        }

        if (fontSizeParam) {
            const parsedFontSize = parseInt(fontSizeParam, 10);
            if (!isNaN(parsedFontSize)) {
                setFontSize(parsedFontSize); // ✅ Set fontSize if valid
            }
        }
    }, [router.query]);

    useEffect(() => {
        if (siteKey) {
            const loadSiteData = async () => {
                setLoading(true);
                setError(null);

                try {
                    const response = await fetch(`/industry/${industrySector}/${siteKey}.json`);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch site data for ${siteKey}`);
                    }
                    const data = await response.json();
                    setSiteData(data[siteKey]);
                } catch (err) {
                    setError(`Error: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            };

            loadSiteData();
        }
    }, [siteKey]);

    const imageRef = useRef<HTMLImageElement>(null);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    const sortedElements = [...(siteData?.elements || [])]
        .filter(el => Array.isArray(el.boundingArea) && el.boundingArea.length === 4)
        .sort((a, b) => {
            const [ax1, ay1] = a.boundingArea;
            const [bx1, by1] = b.boundingArea;
            if (typeof ax1 !== 'number' || typeof ay1 !== 'number' || typeof bx1 !== 'number' || typeof by1 !== 'number') {
                return 0;
            }
            return ay1 - by1 || ax1 - bx1;
        });

    const sortedSiteData = { ...siteData, elements: sortedElements };
    console.log("sortedSiteData2 Keywords (inside component):", sortedSiteData?.keywords);

    return (
        <>
            <main className="" style={{ backgroundColor: bgColor, }}>
                <div className="" style={{ margin: 0, backgroundColor: bgColor, width: screenshotWidth + (PADDING * 2), height: screenshotHeight + (PADDING * 2), borderRadius: 8 }}>
                    <div className="relative order-1 order-lg-2 pb-15">
                        <div className="d-block d-lg-none pb-5">
                            <SeoAuditHeader siteKey={siteKey!} />
                        </div>
                        <ScreenCanvas fontSize={fontSize} padding={PADDING} extraVerticalPadding={extraVerticalPadding} industrySector={industrySector} domain={siteKey} siteData={sortedSiteData} imageRef={imageRef} screenshotWidth={screenshotWidth} screenshotHeight={screenshotHeight} />
                    </div>
                    <div dangerouslySetInnerHTML={{
                        __html: `<style>
                        header,[data-testid="injected-notifications-test-ids-open-notification"] {
                            display: none !important; left: -9999 !important
                        }
                        </style>`,
                    }}>
                    </div></div>
            </main>
        </>
    );
};

export default WebsiteTester;
